import React from 'react';
import { Badge } from './badge';

import {
  CheckCircledIcon,
  CrossCircledIcon,
  UpdateIcon,
  TimerIcon,
  BarChartIcon,
  ExclamationTriangleIcon,
  MinusCircledIcon,
  StopIcon
} from '@radix-ui/react-icons';
import { cn } from '../../lib/utils';
import { STATUS_CARDS, normalizeStatus } from '../../utils/webcheckinStatus';

// Same colours as the status chips (utils/statusUtils.js).
const CARD_STYLE = {
  all: {
    icon: <BarChartIcon className="h-5 w-5" />,
    className: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
    badgeClassName: 'bg-gray-100 text-gray-800',
    textColor: 'text-gray-900'
  },
  SCHEDULED: {
    icon: <TimerIcon className="h-5 w-5" />,
    className: 'border-yellow-200 hover:border-yellow-300 hover:bg-yellow-50',
    badgeClassName: 'bg-yellow-100 text-yellow-800',
    textColor: 'text-yellow-700'
  },
  IN_PROGRESS: {
    icon: <UpdateIcon className="h-5 w-5" />,
    className: 'border-blue-200 hover:border-blue-300 hover:bg-blue-50',
    badgeClassName: 'bg-blue-100 text-blue-800',
    textColor: 'text-blue-700'
  },
  COMPLETED: {
    icon: <CheckCircledIcon className="h-5 w-5" />,
    className: 'border-green-200 hover:border-green-300 hover:bg-green-50',
    badgeClassName: 'bg-green-100 text-green-800',
    textColor: 'text-green-700'
  },
  FAILED: {
    icon: <ExclamationTriangleIcon className="h-5 w-5" />,
    className: 'border-orange-200 hover:border-orange-300 hover:bg-orange-50',
    badgeClassName: 'bg-orange-100 text-orange-800',
    textColor: 'text-orange-700'
  },
  FAILED_BY_ADMIN: {
    icon: <CrossCircledIcon className="h-5 w-5" />,
    className: 'border-red-200 hover:border-red-300 hover:bg-red-50',
    badgeClassName: 'bg-red-100 text-red-800',
    textColor: 'text-red-700'
  },
  REJECTED: {
    icon: <StopIcon className="h-5 w-5" />,
    className: 'border-purple-200 hover:border-purple-300 hover:bg-purple-50',
    badgeClassName: 'bg-purple-100 text-purple-800',
    textColor: 'text-purple-700'
  },
  CANCELLED: {
    icon: <MinusCircledIcon className="h-5 w-5" />,
    className: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
    badgeClassName: 'bg-gray-100 text-gray-800',
    textColor: 'text-gray-700'
  }
};

/**
 * Status Cards component using shadcn/ui components
 * Displays status counts with improved spacing and visual hierarchy
 * 
 * @param {Object} props - Component props
 * @param {Array} props.checkIns - Array of check-in objects
 * @param {string} props.statusFilter - Current status filter ('all' or a status code)
 * @param {Function} props.onStatusClick - Callback when status card is clicked
 * @param {boolean} props.loading - Loading state
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} StatusCards component
 */
const StatusCards = ({
  checkIns = [],
  statusFilter = 'all',
  onStatusClick,
  loading = false,
  className = '',
  ...props
}) => {
  // Count check-ins by status code (every status gets its own card).
  const counts = checkIns.reduce(
    (acc, checkIn) => {
      const status = normalizeStatus(checkIn.status);
      if (status in acc) acc[status] += 1;
      return acc;
    },
    { all: checkIns.length, ...Object.fromEntries(STATUS_CARDS.slice(1).map(({ key }) => [key, 0])) }
  );

  const statusCards = STATUS_CARDS.map((card) => ({ ...card, count: counts[card.key], ...CARD_STYLE[card.key] }));

  return (
    <div className={cn(
      "grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-4",
      className
    )} {...props}>
      {statusCards.map((card) => (
        <div
          key={card.key}
          onClick={() => !loading && onStatusClick?.(card.key)}
          className={cn(
            "bg-white border rounded-lg p-4 min-h-[80px] cursor-pointer",
            "transition-all duration-200 hover:shadow-md",
            "flex flex-col items-center justify-center gap-2",
            statusFilter === card.key 
              ? "ring-2 ring-primary ring-offset-1 border-primary" 
              : card.className,
            loading && "opacity-50 cursor-not-allowed"
          )}
        >
          {/* Icon and Label Row */}
          <div className="flex items-center gap-2">
            {React.cloneElement(card.icon, { 
              className: cn(
                "h-4 w-4 flex-shrink-0",
                statusFilter === card.key ? "text-primary" : card.textColor
              )
            })}
            <span className={cn(
              "text-sm font-medium",
              statusFilter === card.key ? "text-primary" : "text-gray-600"
            )}>
              {card.label}
            </span>
          </div>

          {/* Count */}
          <Badge
            variant="secondary"
            className={cn(
              "text-lg font-bold px-3 py-0.5 min-w-[40px] text-center",
              statusFilter === card.key
                ? "bg-primary text-primary-foreground"
                : card.badgeClassName
            )}
          >
            {loading ? '...' : card.count}
          </Badge>
        </div>
      ))}
    </div>
  );
};

export default StatusCards;
